import React, { useEffect } from "react";
import { useTransactionFilters } from "./hooks/useTransactionFilters";
import { useTransactionData } from "./hooks/useTransactionData";
import { useReassignTransactionModal } from "./hooks/useReassignTransactionModal";
import { useSplitTransactionModal } from "./hooks/useSplitTransactionModal";
import { useTransactionDetailsModal } from "./hooks/useTransactionDetailsModal";
import { useTransactionExport } from "./hooks/useTransactionExport";
import { useUserRole } from "./hooks/useUserRole";
import { useIsGamingMerchant } from "../../hooks/useIsGamingMerchant";
import { useSplitTransactionEnabled } from "../../hooks/useSplitTransactionEnabled";
import useMerchantFilterOptions from "../shared/useMerchantFilterOptions";
import TransactionsPageContent from "./TransactionsPageContent";

const TransactionsPage = () => {
  const { isSuperAdmin, isMerchantAdmin } = useUserRole();
  const { isGamingMerchant } = useIsGamingMerchant();
  const splitTransactionEnabled = useSplitTransactionEnabled();
  const canSplitTransaction = isMerchantAdmin && splitTransactionEnabled;
  const canSeeKraFee = isSuperAdmin || isGamingMerchant;
  const {
    filters,
    pagination,
    apiFilters,
    managementApiFilters,
    setPagination,
    resetPagination,
    searchQuery,
    dateRange,
    serviceFilter,
    merchantId,
    setSearchQuery,
    setDateRange,
    setServiceFilter,
    setMerchantId,
  } = useTransactionFilters(isSuperAdmin);

  const {
    options: merchantOptions,
    isLoading: merchantOptionsLoading,
    isError: isMerchantOptionsError,
  } = useMerchantFilterOptions(isSuperAdmin);

  const {
    transactions,
    totalElements,
    totalPages,
    isLoading,
    isFetching,
    isError,
    refetch,
    noTransactions,
  } = useTransactionData({
    apiFilters,
    managementApiFilters,
    isSuperAdmin,
  });

  const detailsModal = useTransactionDetailsModal();
  const reassignModal = useReassignTransactionModal(merchantOptions);
  const splitModal = useSplitTransactionModal();

  const { handleExportPdf, handleExportExcel, isExporting } =
    useTransactionExport({
      isSuperAdmin,
      searchQuery,
      dateRange,
      serviceCodes: apiFilters.serviceCodes,
      merchantId,
      page: pagination.pageIndex,
      size: pagination.pageSize,
    });
  useEffect(() => {
    resetPagination();
  }, [
    filters.searchQuery,
    filters.dateRange.startDate,
    filters.dateRange.endDate,
    filters.serviceFilter,
    filters.merchantId,
    isSuperAdmin,
    resetPagination,
  ]);

  const filterProps = {
    searchQuery,
    setSearchQuery,
    dateRange,
    setDateRange,
    serviceFilter,
    setServiceFilter,
    merchantId,
    setMerchantId,
  };

  const dataProps = {
    transactions,
    totalElements,
    totalPages,
    noTransactions,
    isLoading,
    isFetching,
    isError,
    onRetry: refetch,
  };

  const paginationProps = {
    pagination,
    setPagination,
  };

  const merchantProps = {
    merchantOptions,
    merchantOptionsLoading,
    isMerchantOptionsError,
  };

  const exportProps = {
    handleExportPdf,
    handleExportExcel,
    isExporting,
  };

  const modals = {
    details: detailsModal,
    reassign: reassignModal,
    split: splitModal,
  };

  return (
    <TransactionsPageContent
      filters={filterProps}
      data={dataProps}
      paginationProps={paginationProps}
      merchants={merchantProps}
      exportProps={exportProps}
      modals={modals}
      isSuperAdmin={isSuperAdmin}
      canSplitTransaction={canSplitTransaction}
      canSeeKraFee={canSeeKraFee}
    />
  );
};

export default TransactionsPage;
